import { motion } from "framer-motion";
import { Camera, Video, Users, ArrowRight } from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";

const ease = [0.22, 1, 0.36, 1];

const PURPLE = "#A855F7";

export const MotoHub = () => {
  const { t } = useLanguage();

  const CARDS = [
    { icon: Camera, title: t.motoHub.f1.title, desc: t.motoHub.f1.desc, key: "photos" },
    { icon: Video, title: t.motoHub.f2.title, desc: t.motoHub.f2.desc, key: "videos" },
    { icon: Users, title: t.motoHub.f3.title, desc: t.motoHub.f3.desc, key: "groups" },
  ];

  return (
    <section
      id="motohub"
      data-testid="motohub-section"
      className="relative py-20 sm:py-28 lg:py-32 overflow-hidden"
    >
      {/* Purple ambient glow */}
      <div
        className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 w-[720px] h-[720px] rounded-full blur-[140px] pointer-events-none opacity-40"
        style={{ background: "radial-gradient(circle, rgba(168,85,247,0.45) 0%, rgba(124,58,237,0.12) 45%, transparent 70%)" }}
        aria-hidden="true"
      />

      <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ y: 18, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease }}
          className="max-w-2xl"
        >
          <div className="inline-flex items-center gap-2 rounded-full border border-[#A855F7]/30 bg-[#A855F7]/10 backdrop-blur-xl px-3 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#A855F7] shadow-[0_0_10px_rgba(168,85,247,0.9)]" />
            <span className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#C084FC]">
              {t.motoHub.eyebrow}
            </span>
          </div>
          <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-white mt-4 tracking-tight">
            {t.motoHub.title1}
            <br />
            <span className="bg-gradient-to-r from-[#C084FC] to-[#A855F7] bg-clip-text text-transparent">
              {t.motoHub.title2}
            </span>
          </h2>
          <p className="mt-4 text-white/65 text-base sm:text-lg leading-relaxed">
            {t.motoHub.subhead}
          </p>
        </motion.div>

        {/* Cards */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-5">
          {CARDS.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div
                key={c.key}
                initial={{ y: 22, opacity: 0 }}
                whileInView={{ y: 0, opacity: 1 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, ease, delay: 0.1 + i * 0.1 }}
                data-testid={`motohub-card-${c.key}`}
                className="group relative rounded-[22px] border border-white/10 bg-white/[0.04] backdrop-blur-xl p-6 sm:p-7 transition-colors duration-200 hover:border-[#A855F7]/40 hover:bg-[#A855F7]/[0.06]"
              >
                <span
                  className="absolute -inset-px rounded-[22px] opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none"
                  style={{ boxShadow: "0 0 40px rgba(168,85,247,0.18)" }}
                  aria-hidden="true"
                />
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl border border-[#A855F7]/30 bg-[#A855F7]/12 text-[#C084FC]">
                  <Icon className="w-6 h-6" />
                </div>
                <h3 className="font-display font-semibold text-white text-lg sm:text-xl mt-5">
                  {c.title}
                </h3>
                <p className="mt-2 text-sm text-white/60 leading-relaxed">{c.desc}</p>
              </motion.div>
            );
          })}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ y: 16, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease, delay: 0.15 }}
          className="mt-10 flex flex-wrap items-center gap-3"
        >
          <button
            data-testid="motohub-cta-button"
            onClick={() => {
              const el = document.querySelector("#download");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }}
            className="inline-flex items-center gap-2 rounded-xl bg-[#A855F7] text-white font-semibold px-5 py-3 text-sm hover:bg-[#B975F9] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#A855F7]/70 active:translate-y-[1px]"
          >
            {t.motoHub.cta}
            <ArrowRight className="w-4 h-4" />
          </button>
          <span className="inline-flex items-center gap-2 rounded-full border border-[#A855F7]/30 bg-[#A855F7]/10 px-3 py-1.5">
            <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#C084FC]">
              {t.motoHub.badge}
            </span>
          </span>
        </motion.div>
      </div>
    </section>
  );
};
